/**
 * Gates: the whole of progression, in one small function.
 *
 * A gate asks whether the player already holds what something requires. Signals, dialogue nodes,
 * dialogue options and endings all use the same shape, so "you cannot hear this until you know
 * that" is one concept rather than four.
 *
 * There is deliberately no scripting language. An author declares what is required, never how to
 * compute it — which is what makes a pack *analysable*: because a gate is a fixed set of clue and
 * event ids, the validator can search the reachable state space and prove a mystery is
 * completable before a player ever tries. A pack with `"if": "player.clues.length > 3"` in it
 * could not be checked, only run.
 */
import type { PackEffects, PackGate } from '../content/schema.js';

/** What the player currently holds. Both sets are ids from the pack. */
export interface StoryState {
  clues: ReadonlySet<string>;
  events: ReadonlySet<string>;
}

export function emptyState(): StoryState {
  return { clues: new Set(), events: new Set() };
}

/** Build a state from plain arrays (what the save file holds). */
export function stateOf(clues: Iterable<string> = [], events: Iterable<string> = []): StoryState {
  return { clues: new Set(clues), events: new Set(events) };
}

/**
 * Does this gate pass?
 *
 * An absent gate always passes — most content is ungated, and requiring authors to write
 * `"requires": {}` everywhere would be noise. Every clause that *is* present must hold; clauses
 * are ANDed, and `anyClues` is the one disjunction (a mystery with two ways in).
 */
export function gatePasses(gate: PackGate | undefined, state: StoryState): boolean {
  if (!gate) return true;

  if (gate.clues?.some((clue) => !state.clues.has(clue))) return false;
  if (gate.events?.some((event) => !state.events.has(event))) return false;

  if (gate.anyClues && gate.anyClues.length > 0) {
    if (!gate.anyClues.some((clue) => state.clues.has(clue))) return false;
  }

  if (gate.notClues?.some((clue) => state.clues.has(clue))) return false;
  if (gate.notEvents?.some((event) => state.events.has(event))) return false;

  return true;
}

/**
 * Why a gate did not pass — the ids the player is still missing.
 *
 * Used by the validator to explain an unreachable node, and by the authoring preview. Never shown
 * to a player: knowing exactly which clue you lack would collapse the mystery into a checklist.
 */
export function missingFor(gate: PackGate | undefined, state: StoryState): string[] {
  if (!gate) return [];
  const missing: string[] = [];

  for (const clue of gate.clues ?? []) if (!state.clues.has(clue)) missing.push(clue);
  for (const event of gate.events ?? []) if (!state.events.has(event)) missing.push(event);
  if (gate.anyClues?.length && !gate.anyClues.some((clue) => state.clues.has(clue))) {
    missing.push(...gate.anyClues);
  }
  return missing;
}

/**
 * Apply effects, returning a new state.
 *
 * Immutable on purpose: the reachability search explores thousands of hypothetical states, and a
 * mutating apply would mean defensive copying at every branch.
 */
export function applyEffects(state: StoryState, effects: PackEffects | undefined): StoryState {
  if (!effects || (!effects.clues?.length && !effects.events?.length)) return state;

  const clues = new Set(state.clues);
  const events = new Set(state.events);
  for (const clue of effects.clues ?? []) clues.add(clue);
  for (const event of effects.events ?? []) events.add(event);
  return { clues, events };
}

/** Would applying these effects change anything? Lets callers skip a pointless save write. */
export function effectsWouldChange(state: StoryState, effects: PackEffects | undefined): boolean {
  if (!effects) return false;
  return (
    (effects.clues ?? []).some((clue) => !state.clues.has(clue)) ||
    (effects.events ?? []).some((event) => !state.events.has(event))
  );
}

/** Every clue and event id a gate mentions, in any clause. */
export function gateReferences(gate: PackGate | undefined): { clues: string[]; events: string[] } {
  if (!gate) return { clues: [], events: [] };
  return {
    clues: [...(gate.clues ?? []), ...(gate.anyClues ?? []), ...(gate.notClues ?? [])],
    events: [...(gate.events ?? []), ...(gate.notEvents ?? [])],
  };
}

/**
 * Does this gate contain a negative clause?
 *
 * Reachability analysis needs to know: a purely positive pack is monotonic — the player's state
 * only grows, so anything ever reachable stays reachable, and a single forward closure settles
 * it. `notClues`/`notEvents` break that (learning something can *close* a door), so a pack using
 * them needs the more careful search. Knowing which kind of pack we have keeps the common case
 * fast and the hard case correct.
 */
export function isNegativeGate(gate: PackGate | undefined): boolean {
  return Boolean(gate?.notClues?.length || gate?.notEvents?.length);
}
